import React, { useCallback, useMemo } from 'react';

import type { Props as TextInputProps } from '../TextInput/TextInput';
import TextInput from '../TextInput/TextInput';
import BaseInputIcon from '../BaseInput/BaseInputIcon';

export type Props = TextInputProps & {
  validationFn?: (value: string) => boolean;
  minLength?: number;
  maxLength?: number;
};

const TextInputWithValidationCheck = (props: Props): JSX.Element => {
  const {
    validationFn = () => true,
    minLength = 0,
    maxLength = Infinity,
    value,
    onChange,
    pattern,
    ...inputProps
  } = props;

  const isInputValid = useCallback(
    (val = ''): boolean => {
      const isValidByMinLength = val.length >= minLength;
      const isValidByMaxLength = val.length <= maxLength;
      const isValidByPattern = pattern ? new RegExp(pattern).test(val) : true;
      const isValidByFunction = validationFn(val);

      return (
        val.length > 0 &&
        isValidByMinLength &&
        isValidByMaxLength &&
        isValidByPattern &&
        isValidByFunction
      );
    },
    [validationFn, maxLength, minLength, pattern]
  );

  const validInputIcon = useMemo(
    () =>
      isInputValid(value) ? (
        <BaseInputIcon
          aria-label="Valid"
          icon="checkmarkCircleInverted"
          color={{
            type: 'theme',
            colorLegacy: 'Green/50',
            colorNext: 'success/default',
          }}
        />
      ) : null,
    [value, isInputValid]
  );

  const onChangeFn = (newInputValue: string) => {
    if (onChange) onChange(newInputValue);
  };

  return (
    <TextInput
      {...inputProps}
      value={value}
      adornmentRight={validInputIcon}
      onChange={onChangeFn}
    />
  );
};

export default TextInputWithValidationCheck;
